"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHVACSensorsByGroup = void 0;
const react_1 = require("react");
const types_1 = require("../../types");
const sensorInclusion_1 = require("../../utils/sensorInclusion");
const useSensors_1 = require("../useSensors");
const HVAC_SENSOR_TYPES = [
    types_1.SensorType.HVAC_CONDENSER_MONITOR,
    types_1.SensorType.HVAC_AIRHANDLER_MONITOR,
];
const useHVACSensorsByGroup = (selectedHubId, 
/**
 * Caller-supplied sensor list. Pass this to (a) include linked gateways'
 * HVAC sensors — they carry their own hubId — and (b) skip the redundant
 * per-hub fetch this hook would otherwise fire on top of the caller's.
 */
allSensors) => {
    const { sensors: fetchedSensors, loading: fetchLoading } = (0, useSensors_1.useSensors)(allSensors ? undefined : selectedHubId);
    const sensors = allSensors ?? fetchedSensors;
    const sensorsLoading = allSensors ? false : fetchLoading;
    const lastCalculationTime = (0, react_1.useRef)(0);
    const cachedResult = (0, react_1.useRef)([]);
    const THROTTLE_MS = 1000;
    const sensorKey = (0, react_1.useMemo)(() => {
        const now = Date.now();
        if (now - lastCalculationTime.current < THROTTLE_MS) {
            return 'throttled';
        }
        if (!selectedHubId || !sensors.length)
            return '';
        // No hubId predicate — `sensors` is already scoped (either fetched for
        // this hub, or the caller's household list including linked gateways).
        const hvacSensors = sensors.filter((sensor) => (0, sensorInclusion_1.isDashboardSensor)(sensor) && HVAC_SENSOR_TYPES.includes(sensor.type));
        // status is in the key so an ACTIVE→INACTIVE sweep flip (offline
        // detection) recomputes the groups even when nothing else changed.
        const key = hvacSensors
            .map((s) => `${s.id}-${s.groupId}-${s.name}-${s.status}`)
            .sort()
            .join('|');
        lastCalculationTime.current = now;
        return key;
    }, [sensors, selectedHubId]);
    const hvacSensorGroups = (0, react_1.useMemo)(() => {
        if (sensorKey === 'throttled') {
            return cachedResult.current;
        }
        if (!selectedHubId || !sensors.length)
            return [];
        // No hubId predicate — `sensors` is already scoped (either fetched for
        // this hub, or the caller's household list including linked gateways).
        const hvacSensors = sensors.filter((sensor) => (0, sensorInclusion_1.isDashboardSensor)(sensor) && HVAC_SENSOR_TYPES.includes(sensor.type));
        const groupMap = new Map();
        hvacSensors.forEach((sensor) => {
            const groupId = sensor.groupId ?? 'default-sensor-group-id';
            if (!groupId)
                return;
            if (!groupMap.has(groupId)) {
                // Both members of a pair live on the same hub, so the first sensor to
                // land settles it. `selectedHubId` is only a fallback for legacy rows
                // with no hubId set.
                groupMap.set(groupId, { groupId, hubId: sensor.hubId ?? selectedHubId ?? '' });
            }
            const group = groupMap.get(groupId);
            let sensorConfig = sensor.sensorGroupConfiguration;
            if (typeof sensorConfig === 'string') {
                try {
                    sensorConfig = JSON.parse(sensorConfig);
                }
                catch (_error) {
                    sensorConfig = null;
                }
            }
            let isIndoor;
            if (sensorConfig?.indoor !== null && sensorConfig?.indoor !== undefined) {
                isIndoor = sensorConfig.indoor;
            }
            else if (sensor.type === types_1.SensorType.HVAC_AIRHANDLER_MONITOR) {
                isIndoor = true;
            }
            else if (sensor.type === types_1.SensorType.HVAC_CONDENSER_MONITOR) {
                isIndoor = false;
            }
            else {
                isIndoor = true;
            }
            if (isIndoor) {
                group.indoorSensor = sensor;
            }
            else {
                group.outdoorSensor = sensor;
            }
        });
        // Order by location, not by arrival: sensors come back in DynamoDB id-hash
        // order, so a demo mirror and its source hub listed the same systems in
        // different orders. groupId breaks ties so unlabeled systems stay put.
        const label = (g) => (g.indoorSensor?.location || g.outdoorSensor?.location || '').trim();
        const result = Array.from(groupMap.values()).sort((a, b) => label(a).localeCompare(label(b)) || a.groupId.localeCompare(b.groupId));
        cachedResult.current = result;
        return result;
    }, [sensorKey, selectedHubId]);
    return {
        hvacSensorGroups,
        loading: !!sensorsLoading,
        error: null,
    };
};
exports.useHVACSensorsByGroup = useHVACSensorsByGroup;
